import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Chip, 
  Button,
  IconButton,
  useTheme,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Avatar
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CodeIcon from '@mui/icons-material/Code';
import { motion } from 'framer-motion';
import { styled } from '@mui/material/styles';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  position: 'relative',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: theme.palette.divider,
  },
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.shadows[1],
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const ProjectContent = styled(CardContent)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
}));

const ProjectActions = styled(CardActions)(({ theme }) => ({
  padding: theme.spacing(0, 1.5, 1.5),
  '& .MuiButton-root': {
    padding: theme.spacing(0.5, 1.5),
  },
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
}));

const ProjectIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  width: 36,
  height: 36,
}));

const TechChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(100, 181, 246, 0.1)' 
    : 'rgba(46, 80, 119, 0.08)',
  color: theme.palette.primary.main,
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  height: '24px',
  fontSize: '0.7rem',
  fontWeight: 500,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  display: 'inline-block',
  marginBottom: theme.spacing(1),
  fontWeight: 700,
  letterSpacing: '-0.5px',
  color: theme.palette.text.primary,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: 0,
    width: 40,
    height: 3,
    backgroundColor: theme.palette.primary.main,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0.5, 1),
  fontSize: '0.8rem',
  fontWeight: 500,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
}));

const FeaturedProject = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  marginBottom: theme.spacing(4),
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  transition: 'all 0.3s ease',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const FeaturedProjectContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const FeaturedTag = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  zIndex: 2,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
}));

const Projects = () => {
  const theme = useTheme();
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 15,
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 50,
        damping: 12,
      }
    },
  };

  // Featured Projects (more detailed, current work)
  const featuredProjects = [
    {
      id: 1,
      title: "Realtime Inventory Sync Up With kafka",
      description: "Led the development of a comprehensive Kafka based real time inventory sync up solution for an e-commerce company, focusing on microservices architecture and scalable cloud infrastructure.",
      tech: ["Java", "Spring Boot", "Kubernetes", "Kafka",],
      links: {
      }
    },
    {
      id: 2,
      title: "Scalable Notification Microservices for E-commerce",
      description: "Designed and built a high-throughput notification microservices solution for an e-commerce company. This helped to improve customer satisfaction and order processing times by 20%.",
      tech: ["Java", "Spring Boot", "PostgreSQL","Kubernetes"],
      links: {
        
      }
    }
  ];

  // Regular projects (past work)
  const regularProjects = [
    // {
    //   id: 3,
    //   title: "E-Commerce Microservices",
    //   description: "Built a scalable e-commerce platform using microservices architecture, handling product catalog, inventory, and payment integration.",
    //   tech: ["Node.js", "Express", "MongoDB", "Redis", "Docker"],
    //   links: {
    //     github: "https://github.com/username/ecommerce-ms",
    //     live: "https://example-ecommerce.com"
    //   }
    // },
    // {
    //   id: 4,
    //   title: "Real-time Chat Application",
    //   description: "Developed a real-time messaging platform with end-to-end encryption, message persistence, and multi-device synchronization.",
    //   tech: ["React", "Node.js", "Socket.IO", "Redis", "AWS"],
    //   links: {
    //     github: "https://github.com/username/chat-app",
    //     live: "https://example-chat.com"
    //   }
    // },
    // {
    //   id: 5,
    //   title: "Distributed Task Scheduler",
    //   description: "Created a fault-tolerant distributed task scheduling system that handles millions of recurring jobs with precise timing and failure recovery.",
    //   tech: ["Go", "etcd", "Redis", "gRPC", "Kubernetes"],
    //   links: {
    //     github: "https://github.com/username/distributed-scheduler",
    //     live: ""
    //   }
    // },
    // {
    //   id: 6,
    //   title: "IoT Monitoring Dashboard",
    //   description: "Built a real-time monitoring dashboard for IoT devices, featuring data visualization, anomaly detection, and automated alerting.",
    //   tech: ["React", "D3.js", "Node.js", "MQTT", "AWS IoT"],
    //   links: {
    //     github: "https://github.com/username/iot-dashboard",
    //     live: "https://example-iot.com"
    //   }
    // }
  ];

  return (
    <SectionContainer id="projects">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <SectionTitle variant="h2" gutterBottom sx={{ mb: 1, '&::after': { left: 'calc(50% - 20px)' } }}>
            My Projects
          </SectionTitle>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: '1rem'
            }}
          >
            A showcase of my work spanning enterprise systems, distributed architectures, and data processing platforms.
          </Typography>
        </Box>

        <Box sx={{ mb: 5 }}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 30,
                height: 2,
                backgroundColor: theme.palette.secondary.main,
              }
            }}
          >
            Featured Work
          </Typography>

          <Grid container spacing={3}>
            {featuredProjects.map((project) => (
              <Grid item xs={12} md={6} key={project.id}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <FeaturedProject>
                    <FeaturedProjectContent>
                      <ProjectHeader>
                        <ProjectIcon>
                          <FolderOpenIcon />
                        </ProjectIcon>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {project.links.github && (
                            <IconButton 
                              size="small" 
                              color="primary"
                              href={project.links.github}
                              target="_blank"
                              aria-label="GitHub repository"
                            >
                              <GitHubIcon fontSize="small" />
                            </IconButton>
                          )}
                          {project.links.live && (
                            <IconButton 
                              size="small" 
                              color="primary"
                              href={project.links.live}
                              target="_blank"
                              aria-label="Live demo"
                            >
                              <LaunchIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </ProjectHeader>
                      
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 1 }}>
                        {project.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {project.description}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                        {project.tech.map(tech => (
                          <TechChip key={tech} label={tech} />
                        ))}
                      </Box>
                    </FeaturedProjectContent>
                  </FeaturedProject>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              mb: 3, 
              fontWeight: 600,
              position: 'relative',
              display: 'inline-block',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 30,
                height: 2,
                backgroundColor: theme.palette.secondary.main,
              }
            }}
          >
            Other Projects
          </Typography>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Grid container spacing={3}>
              {regularProjects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <motion.div variants={itemVariants}>
                    <ProjectCard>
                      <ProjectContent>
                        <ProjectHeader>
                          <ProjectIcon>
                            <CodeIcon />
                          </ProjectIcon>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {project.links.github && (
                              <IconButton 
                                size="small" 
                                color="primary"
                                href={project.links.github}
                                target="_blank"
                                aria-label="GitHub repository"
                              >
                                <GitHubIcon fontSize="small" />
                              </IconButton>
                            )}
                            {project.links.live && (
                              <IconButton 
                                size="small" 
                                color="primary"
                                href={project.links.live}
                                target="_blank"
                                aria-label="Live demo"
                              >
                                <LaunchIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        </ProjectHeader>
                        
                        <Typography gutterBottom variant="h6" component="h2" sx={{ fontWeight: 600, mt: 1 }}>
                          {project.title}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {project.description}
                        </Typography>
                      </ProjectContent>
                      <Divider />
                      <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap' }}>
                        {project.tech.slice(0, 3).map((tech) => (
                          <TechChip key={tech} label={tech} size="small" />
                        ))}
                        {project.tech.length > 3 && (
                          <TechChip label={`+${project.tech.length - 3}`} size="small" />
                        )}
                      </Box>
                    </ProjectCard>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="outlined" 
              color="primary"
              endIcon={<GitHubIcon />}
              href="https://github.com/kumarav1nash"
              target="_blank"
            >
              Browse All Projects
            </Button>
          </Box>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default Projects;